import React, { useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { Outlet } from 'react-router-dom';
import { SalesforceContextProvider } from '../../contexts/SalesforceContext';
import { getAuthStore } from '../../graphql/store';
import GET_CHAT_AUTHORIZATION_TOKEN from '../../graphql/querys/salesforce/getChatAuthorizationToken';

import './style.css';

const SalesforceApp = () => {
  const {
    data: { uid },
  } = useReactiveVar(getAuthStore);
  const [initializedSalesforceChat, setInitializedSalesforceChat] = useState(false);
  const [initializationToken, setInitializationToken] = useState('');

  const [getChatAuthorizationToken, { data, loading }] = useLazyQuery(
    GET_CHAT_AUTHORIZATION_TOKEN.query,
    { skip: uid === '', ...GET_CHAT_AUTHORIZATION_TOKEN.policies },
  );

  const initEmbeddedMessaging = useCallback(() => {
    try {
      window.embeddedservice_bootstrap.settings.language = 'es_MX';
      window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;
      window.embeddedservice_bootstrap.init(
        '00DKb000000OlrI',
        'Tucar_Customer_Support_Test',
        'https://tucar.my.site.com/ESWTucarCustomerSupport1721763001554',
        { scrt2URL: 'https://tucar.my.salesforce-scrt.com' },
      );
      setInitializedSalesforceChat(true);
    } catch (err) {
      setInitializedSalesforceChat(false);
    }
  }, []);

  const handleEmbeddedMessagingReady = useCallback(() => {
    const identityToken = initializationToken;
    window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
      identityTokenType: 'JWT',
      identityToken,
    });
  }, [initializationToken]);

  const handleIdentityTokenExpired = useCallback(async () => {
    setInitializedSalesforceChat(false);
    await getChatAuthorizationToken({
      variables: { driverId: uid || '' },
    });
  }, [getChatAuthorizationToken, uid]);

  useEffect(() => {
    getChatAuthorizationToken({ variables: { driverId: uid || '' } });
  }, []);

  useEffect(() => {
    if (!loading && data?.getChatAuthorizationToken) {
      setInitializationToken(data.getChatAuthorizationToken?.jwt || '');
    }
  }, [loading]);

  useEffect(() => {
    if (initializationToken) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src =
        'https://tucar.my.site.com/ESWTucarCustomerSupport1721763001554/assets/js/bootstrap.min.js';
      script.onload = initEmbeddedMessaging;
      document.body.appendChild(script);
      return () => document.body.removeChild(script);
    }
    return undefined;
  }, [initializationToken]);

  useEffect(() => {
    if (initializationToken) {
      window.addEventListener('onEmbeddedMessagingReady', handleEmbeddedMessagingReady);
      window.addEventListener(
        'onEmbeddedMessagingIdentityTokenExpired',
        handleIdentityTokenExpired,
      );

      return () => {
        window.removeEventListener('onEmbeddedMessagingReady', handleEmbeddedMessagingReady);
        window.removeEventListener(
          'onEmbeddedMessagingIdentityTokenExpired',
          handleIdentityTokenExpired,
        );
      };
    }
    return undefined;
  }, [initializationToken]);

  return (
    <SalesforceContextProvider initializing={loading} initializedChat={initializedSalesforceChat}>
      <Outlet />
    </SalesforceContextProvider>
  );
};

export default SalesforceApp;
